
import React, { useState, useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { Session } from "./utils/session";
import { useSidebar } from '../hooks';

const authHeaders = () => {
  const token = Session.getToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
};


function TeamsForms() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    WorkspaceName: '',
    Description: '',
    IsActive: false,
    OwnerUserID: null,
  });

  const [nameError, setNameError] = useState('');
  const [checkingName, setCheckingName] = useState(false);

  const { toggleSidebar } = useSidebar();

  const handleChange = useCallback((e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);

  const resolveOwnerUserId = useCallback(() => {
    try {
      const rawUser = localStorage.getItem('user') || localStorage.getItem('currentUser');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        return parsed?.userId ?? parsed?.UserID ?? parsed?.id ?? null;
      }
      const direct = localStorage.getItem('userId') || localStorage.getItem('UserID');
      if (direct) return Number(direct);
    } catch (e) {}
    return 1;
  }, []);

  const checkNameExists = useCallback(async (name) => {
    if (!name) return false;
    try {
      setCheckingName(true);
      const resp = await fetch(`${API_BASE_URL}/api/Workspace/Workspaces_List`, {
        headers: authHeaders()
      });
      if (!resp.ok) return false;
      const data = await resp.json();
      if (!Array.isArray(data)) return false;
      return data.some((w) => {
        const n = (w.WorkspaceName ?? w.workspaceName ?? w.name ?? '').toString().trim().toLowerCase();
        return n === name.toString().trim().toLowerCase();
      });
    } catch (e) {
      console.warn('Name check failed', e);
      return false;
    } finally {
      setCheckingName(false);
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (formData.WorkspaceName) {
      const exists = await checkNameExists(formData.WorkspaceName);
      if (exists) {
        setNameError('Name already available');
        alert('Workspace name already available');
        return;
      }
    }
    const ownerId = formData.OwnerUserID ?? resolveOwnerUserId();
    const payload = {
      WorkspaceName: formData.WorkspaceName,
      Description: formData.Description,
      OwnerUserID: ownerId,
      IsActive: !!formData.IsActive,
    };
     try {
      const resp = await fetch(`${API_BASE_URL}/api/Workspace/Insert_Workspace`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => null);
        throw new Error(txt || `Request failed with status ${resp.status}`);
      }
      await resp.json().catch(() => null);
      navigate('/teams');
    } catch (err) {
      console.error('Failed to insert workspace', err);
      alert('Failed to insert workspace: ' + (err.message || err));
    }
  }, [formData, checkNameExists, resolveOwnerUserId, navigate]);

  return (
    <div>
      <div id="sidebar-overlay" />
      <Sidebar />
      <div id="content">
        <div className="d-flex align-items-center mb-3">
          <button type="button" id="sidebarCollapse" className="btn btn-toggle me-3" onClick={toggleSidebar}>
            <i className="fas fa-bars" />
          </button>
          <a href="/" className="text-muted text-decoration-none small"><i className="fas fa-chevron-left" /> Back</a>
        </div>
        <div className="product-page-wrapper">
          <div className="product-page container-fluid">
            <style>{`
            .product-page-wrapper {
              background-color: #f8f9fa;
              padding: 30px 0;
            }
            .main-card {
              margin: 0 auto;
              box-shadow: 0 4px 12px rgba(0,0,0,0.08);
              border: none;
            }
            .btn-primary { background: #0084fe; border-color: #0084fe; color: #fff; }
            .btn-primary:hover, .btn-primary:focus { background: #08539aff; border-color: #08539aff; color: #fff; }
            .btn-outline-secondary { background: transparent; border: 1px solid #ced4da; color: #495057; }
            .btn-outline-secondary:hover { background: rgba(23,165,137,0.06); }

            .member-section {
              margin-bottom: 40px;
              border: 1px solid #eee;
              padding: 16px;
              border-radius: 8px;
              background: #fff;
              box-shadow: 0 1px 3px rgba(0,0,0,0.03);
            }
            .section-header { margin-bottom: 12px; }

            .badge-warning { background: #ffeeba; color: #856404; font-size: 14px; padding: 6px 12px; border-radius: 12px; }
            .badge-secondary { background: #e2e3e5; color: #41464b; font-size: 14px; padding: 6px 12px; border-radius: 12px; }
            .badge-success { background: #d4edda; color: #074885ff; font-size: 14px; padding: 6px 12px; border-radius: 12px; }

            .member-table { width: 100%; border-collapse: collapse; }
            .member-table thead th { color: #6c757d; font-size: 13px; text-transform: uppercase; padding: 10px; border-bottom: 2px solid #f1f1f1; }
            .member-table td { padding: 12px 10px; vertical-align: middle; border-bottom: 1px solid #f6f6f6; }
            .member-table tr.removed-row { opacity: 0.6; }

            .nav-tabs {
              border-bottom: 1px solid #dee2e6;
            }
            .nav-tabs .nav-link {
              border: none;
              border-bottom: 3px solid transparent;
              color: #6c757d;
              font-weight: 500;
            }
            .nav-tabs .nav-link.active {
              border-bottom-color: #0084fe;
              color: #0084fe;
              background-color: transparent;
            }

            .form-check-input:checked{
             background-color: #0084fe;
             border-color: #0084fe;
             }

            input[type="checkbox"] { width: 18px; height: 18px; cursor: pointer; accent-color: #0084fe; }
            .member-table td input[type="checkbox"] { display: inline-block; margin: 0; }

            @media (max-width: 768px) {
              .member-table thead th, .member-table td { font-size: 13px; }
            }
          `}</style>
            <div className="main-card">
              <div className="panel panel-default">
                <div className="panel-body p-0">
                  <div className="mb-0 p-4">
                    <h4 className="mb-3">Create Team</h4>
                    <form onSubmit={handleSubmit} noValidate className="main-card">

                      <div className="tab-content px-4 pt-2">
                        {activeTab === 'general' && (
                          <div className="tab-pane fade show active">
                            <div className="row">
                              <div className="form-group col-md-6 mb-3">
                                <label>Name</label>
                                <input
                                  type="text"
                                  name="WorkspaceName"
                                  className="form-control"
                                  value={formData.WorkspaceName}
                                  onChange={(e) => {
                                    if (nameError) setNameError('');
                                    handleChange(e);
                                  }}
                                  onBlur={async (e) => {
                                    const val = e.target.value && e.target.value.toString().trim();
                                    if (!val) {
                                      setNameError('');
                                      return;
                                    }
                                    setNameError('');
                                    try {
                                      setCheckingName(true);
                                      const exists = await checkNameExists(val);
                                      if (exists) setNameError('Name already available');
                                    } finally {
                                      setCheckingName(false);
                                    }
                                  }}
                                />
                                {nameError && <small className="text-danger d-block mt-1">{nameError}</small>}
                              </div>


                              <div className="form-group col-12  mb-3">
                                <label>Description</label>
                                <textarea name="Description" className="form-control" value={formData.Description} onChange={handleChange} />
                              </div>

                              <div className="form-group col-md-6 d-flex align-items-center">
                                <div className="form-check">
                                  <input
                                    type="checkbox"
                                    name="IsActive"
                                    checked={formData.IsActive}
                                    onChange={handleChange}
                                    className="form-check-input"
                                    id="IsActive"
                                  />
                                  <label htmlFor="IsActive" style={{ marginLeft: '8px' }}>
                                    Is Active
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                      </div>
                      <div className="col-12 mt-3 d-flex px-4" style={{ gap: 8 }}>
                        <button type="submit" className="btn btn-primary" disabled={!!nameError || checkingName}>
                          {checkingName ? 'Checking...' : 'Save'}
                        </button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/teams')}>Cancel</button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TeamsForms;
